import React, { useState, useEffect } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  VStack,
  HStack,
  Text,
  Badge,
  RadioGroup,
  Radio,
  Divider,
  FormErrorMessage,
} from "@chakra-ui/react";

interface InitiateRefundModalProps {
  isOpen: boolean;
  onClose: () => void;
  invoice: any;
  onSubmit: (data: { order_ref_id: string; amount?: number; reason?: string }) => Promise<void>;
  isLoading: boolean;
}

export const InitiateRefundModal: React.FC<InitiateRefundModalProps> = ({
  isOpen,
  onClose,
  invoice,
  onSubmit,
  isLoading,
}) => {
  const originalAmount = invoice?.amount || 0;
  const alreadyRefunded = invoice?.refundedAmount || 0;
  const remainingAmount = Math.max(0, originalAmount - alreadyRefunded);

  const [refundType, setRefundType] = useState<"full" | "partial">("full");
  const [amount, setAmount] = useState<number>(remainingAmount);
  const [reason, setReason] = useState<string>("");
  const [amountError, setAmountError] = useState<string>("");

  useEffect(() => {
    if (invoice) {
      const orig = invoice?.amount || 0;
      const ref = invoice?.refundedAmount || 0;
      const rem = Math.max(0, orig - ref);
      setRefundType("full");
      setAmount(rem);
      setReason("");
      setAmountError("");
    }
  }, [invoice]);

  const handleRefundTypeChange = (type: "full" | "partial") => {
    setRefundType(type);
    if (type === "full") {
      setAmount(remainingAmount);
      setAmountError("");
    }
  };

  const handleAmountChange = (val: number) => {
    setAmount(val);
    if (val <= 0) {
      setAmountError("Refund amount must be greater than ₹0");
    } else if (val > remainingAmount) {
      setAmountError(`Refund amount cannot exceed remaining balance of ₹${remainingAmount}`);
    } else {
      setAmountError("");
    }
  };

  const handleSubmit = async () => {
    if (refundType === "partial" && (amount <= 0 || amount > remainingAmount)) {
      setAmountError(`Amount must be between ₹1 and ₹${remainingAmount}`);
      return;
    }

    const payload: any = {
      order_ref_id: invoice?.paymentProviderId || invoice?._id,
      reason: reason.trim() || undefined,
    };

    if (refundType === "partial") {
      payload.amount = Number(amount);
    } else if (alreadyRefunded > 0) {
      payload.amount = Number(remainingAmount);
    }

    await onSubmit(payload);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg" isCentered>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Initiate Refund</ModalHeader>
        <ModalCloseButton isDisabled={isLoading} />
        <ModalBody>
          <VStack spacing={4} align="stretch">
            {/* Context Card */}
            <VStack p={3} bg="gray.50" borderRadius="md" align="stretch" spacing={2}>
              <HStack justify="space-between">
                <Text fontSize="sm" color="gray.600">Ticket ID:</Text>
                <Badge colorScheme="purple">{invoice?.ticketId || "N/A"}</Badge>
              </HStack>
              <HStack justify="space-between">
                <Text fontSize="sm" color="gray.600">Subscriber:</Text>
                <Text fontSize="sm" fontWeight="medium">{invoice?.subscriberData?.name || "N/A"}</Text>
              </HStack>
              <HStack justify="space-between">
                <Text fontSize="sm" color="gray.600">Service Type:</Text>
                <Text fontSize="sm">{invoice?.orderFor || "N/A"}</Text>
              </HStack>
              <HStack justify="space-between">
                <Text fontSize="sm" color="gray.600">Paid Amount:</Text>
                <Text fontSize="md" fontWeight="bold" color="green.600">₹{originalAmount}</Text>
              </HStack>
              {alreadyRefunded > 0 && (
                <>
                  <HStack justify="space-between">
                    <Text fontSize="sm" color="gray.600">Already Refunded:</Text>
                    <Text fontSize="sm" fontWeight="semibold" color="red.500">₹{alreadyRefunded}</Text>
                  </HStack>
                  <HStack justify="space-between">
                    <Text fontSize="sm" color="gray.600">Remaining Balance:</Text>
                    <Text fontSize="md" fontWeight="bold" color="blue.600">₹{remainingAmount}</Text>
                  </HStack>
                </>
              )}
            </VStack>

            <Divider />

            {/* Refund Type */}
            <FormControl>
              <FormLabel fontSize="sm" fontWeight="bold">Refund Type</FormLabel>
              <RadioGroup value={refundType} onChange={(val: any) => handleRefundTypeChange(val)}>
                <HStack spacing={6}>
                  <Radio value="full">
                    {alreadyRefunded > 0 ? `Remaining Balance (₹${remainingAmount})` : `Full Refund (₹${originalAmount})`}
                  </Radio>
                  <Radio value="partial">Partial Refund</Radio>
                </HStack>
              </RadioGroup>
            </FormControl>

            {/* Partial Amount Input */}
            {refundType === "partial" && (
              <FormControl isInvalid={!!amountError}>
                <FormLabel fontSize="sm" fontWeight="bold">Refund Amount (₹)</FormLabel>
                <Input
                  type="number"
                  value={amount}
                  onChange={(e) => handleAmountChange(Number(e.target.value))}
                  max={remainingAmount}
                  min={1}
                />
                <Text fontSize="xs" color="gray.500" mt={1}>
                  Available to refund: ₹{remainingAmount}
                </Text>
                {amountError && <FormErrorMessage>{amountError}</FormErrorMessage>}
              </FormControl>
            )}

            {/* Reason */}
            <FormControl>
              <FormLabel fontSize="sm" fontWeight="bold">Reason for Refund</FormLabel>
              <Textarea
                placeholder="e.g. Customer cancelled ride, service unavailable, duplicate charge"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={3}
              />
            </FormControl>
          </VStack>
        </ModalBody>

        <ModalFooter gap={2}>
          <Button variant="ghost" onClick={onClose} isDisabled={isLoading}>
            Cancel
          </Button>
          <Button
            colorScheme="blue"
            onClick={handleSubmit}
            isLoading={isLoading}
            loadingText="Initiating..."
            isDisabled={!!amountError || remainingAmount <= 0}
          >
            Confirm Refund
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
